import { History, Pencil, Settings, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';

import { formatDateTime, toIso } from '../../lib/formatDate';
import { Menu } from '../Menu';
import { Tooltip } from '../Tooltip';
import { profileSettingsItems, type ProfileSettingsActions } from './profileSettingsMenu';

/**
 * The identity strip of the **header** profile layout — what this record is, stated once
 * across the full width of the page and visible on every tab below it.
 *
 * It is to `<ProfileHeaderShell>` what `<ProfileIdentity>` is to `<ProfileShell>`: the same
 * job, laid out horizontally. Left is the record — an icon or an avatar, its name, and a
 * middot-joined line of facts that need no label. Right is its state and where it came from.
 *
 * **The sidebar layout may carry it too**, through `<ProfileShell>`'s `header` slot. There it
 * REPLACES `<ProfileIdentity>` rather than sitting above it — the two state the same three
 * things, and a page showing both prints one record twice.
 *
 * **Provenance lives here rather than in the Info tab's Audit card.** Who created the record
 * and when is the one audit fact a reader wants while looking at any other tab, and the
 * header is on screen for all of them; the Audit card carries the rest of the trail.
 * Printing it in both places would be the same value in two slots (CLAUDE.md).
 */
export function ProfileHeader({
  icon,
  avatar,
  title,
  meta = [],
  secondary = [],
  status,
  actions,
  settings,
  createdBy,
  createdAt,
  id,
}: {
  /** Rendered in a tinted disc. A fixed tint, so every header profile reads the same. */
  icon?: ReactNode;
  /**
   * A record whose subject is a PERSON leads with their face instead of a glyph.
   *
   * It replaces the tinted disc rather than sitting in it — an `<Avatar>` already draws its
   * own circle, and nesting one inside the icon disc would paint two. Pass one or the other.
   */
  avatar?: ReactNode;
  /** The record's name. The page's heading. */
  title: string;
  /**
   * Facts that need no label — a code, a request line, a period. Joined with `·` here
   * rather than by each caller, so the line reads identically on every profile. Empty
   * entries are dropped, so an absent fact leaves no orphan separator.
   */
  meta?: ReactNode[];
  /**
   * A SECOND line of facts, under the first.
   *
   * For a fact that is long enough to push the others off the line, or that reads as a
   * different kind of thing from them — an email beside an employee number and a post. Same
   * treatment, same middot join; it is a second line, not a lesser one.
   */
  secondary?: ReactNode[];
  /**
   * Controls that act on the whole record, beside the status pill.
   *
   * A settings cog belongs here rather than in the sidebar: the header is on screen for every
   * panel, and an action about the record must not be somewhere the reader has to go back to.
   * Use the shared `<Menu>` — never a second hand-rolled dropdown.
   */
  actions?: ReactNode;
  /**
   * The record's own settings cog — **the DEFAULT on every profile**, the way it already is on
   * `<ProfileCard>`.
   *
   * Supply the handlers and the header draws the menu: Edit, History Logs, the page's own
   * entries, then Delete last. Each is dropped when its handler is absent and HIDDEN when the
   * reader lacks its key, so a page states what the record can do and the gate decides who
   * sees it.
   *
   * It replaces hand-rolling a `<Menu>` into `actions`, which 25 profile pages were each
   * doing — the same defect `onCreate` had on a panel: *a slot that takes a `ReactNode` where
   * a callback would do*. `actions` remains for a control that is NOT a menu entry (a Publish
   * button beside the cog), and a page passing both gets both.
   */
  settings?: ProfileSettingsActions;
  /**
   * The status pill. The always-on indicator: a `<ProfileBanner>` saying the same thing can
   * be dismissed for the visit, and this survives it.
   */
  status?: ReactNode;
  createdBy?: string | null;
  createdAt?: string | null;
  /*
    THERE IS NO `updatedBy` / `updatedAt` HERE ANY MORE, and their removal is the other half of
    the reversal above rather than tidying.

    The line reads the creation unconditionally, so the pair had nothing left to decide — and a
    prop a component accepts and never reads is the `ExpiryThresholdDays` failure one layer up:
    31 callers were passing two values that changed nothing on screen, and the next reader would
    have had no way to tell that from a component that used them. They were dropped from every
    one of those call sites in the same change.

    The LATEST update is still on the record and still shown — in the Audit card, which holds
    the whole trail, and in the list view's own `Latest update` line.
  */
  id?: string;
}) {
  /*
    `false` and `''` are what a `cond && value` or a `.filter().join()` collapses to.

    A fact EQUAL TO THE TITLE is dropped as well, and that is not tidiness. A title is often
    the end of a fallback chain — the personnel header resolves a name from the identity store
    and falls back to the employee number, which is also the first fact on the line — so
    without this the strip prints one value in two slots for every record whose name has not
    resolved. The guard belongs here rather than at the call site for the reason
    `<ProfileIdentity>`'s does: a caller cannot know what its own fallback resolved to at
    runtime, and the one that forgets is the one that ships it.
  */
  const keep = (fact: ReactNode) =>
    fact !== null &&
    fact !== undefined &&
    fact !== '' &&
    fact !== false &&
    !(typeof fact === 'string' && sameValue(fact, title));

  const facts = meta.filter(keep);
  const moreFacts = secondary.filter(keep);

  /*
    THE LINE STATES THE CREATION, ALWAYS — product call, 29 Sep 2026, in the reader's own
    words: *right side should be status, below it is "created by" not updated by.*

    IT REVERSES THE CALL OF 14 Sep 2026, which was that a header stating a STATUS states the
    latest change under it and falls back to the creation. That argument was real — *a status
    is what it is because of the last thing that happened, so a status with a creation date
    under it answers a question nobody asked* — and it is recorded rather than deleted.

    What it cost is why it was overruled: the line CHANGED SHAPE per record. A record nobody
    had touched read `Created by …` and its neighbour read `Updated by …`, so the one fact a
    reader wanted to compare across two profiles was labelled differently on each — and which
    half they were looking at depended on data rather than on the screen. One verb, always, is
    a line that can be read without first working out which question it is answering.

    THE AUDIT CARD IS WHAT MAKES THAT AFFORDABLE, and the yield recorded there now runs the
    other way: it holds the WHOLE trail, so the update half is one card down rather than
    nowhere — where before this reversal the card had to carry everything precisely BECAUSE
    the header's half was unpredictable.
  */
  /*
    The cog's entries, in the one order every profile uses — Edit, History Logs, the page's
    own, Delete last. Derived here rather than at 25 call sites, which is the whole point.
    The ICONS are supplied rather than chosen inside the helper so that file stays free of
    JSX and keeps fast refresh.
  */
  const settingsItems = settings
    ? profileSettingsItems(settings, {
        edit: <Pencil className="h-4 w-4" aria-hidden="true" />,
        history: <History className="h-4 w-4" aria-hidden="true" />,
        remove: <Trash2 className="h-4 w-4" aria-hidden="true" />,
      })
    : [];

  const showProvenance = Boolean(createdBy) || Boolean(createdAt);

  const actor = createdBy;
  const instant = createdAt;
  const verb = 'Created';

  return (
    <header
      id={id}
      className="flex shrink-0 items-start gap-4 border-b border-slate-200 bg-white px-4 py-3.5"
    >
      {avatar ? <span className="mt-0.5 shrink-0">{avatar}</span> : null}

      {!avatar && icon ? (
        <span
          className="mt-0.5 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700"
          aria-hidden="true"
        >
          {icon}
        </span>
      ) : null}

      <div className="min-w-0 flex-1">
        <h1 className="min-w-0 break-words text-base font-medium text-slate-800">{title}</h1>

        <FactLine facts={facts} />
        <FactLine facts={moreFacts} />
      </div>

      {status || actions || settingsItems.length > 0 || showProvenance ? (
        /*
          The cog is centred against the WHOLE right-hand block rather than riding the status
          line, so it sits on the strip's midline the way the avatar does on the other side.
          It is a record-level control and belongs level with the record, not pinned to the
          top line of a two-line stack.
        */
        <div className="flex shrink-0 items-center gap-2">
          <div className="flex min-w-0 flex-col items-end gap-1 text-right">
            {status ? <div className="flex items-center">{status}</div> : null}

            {showProvenance ? (
              <p className="text-xs text-slate-500">
                {/* No actor recorded still says when — "Created · <date>" reads correctly. */}
                {actor ? `${verb} by ${actor}` : verb}
                {instant ? (
                  <>
                    <span className="px-1 text-slate-300" aria-hidden="true">
                      ·
                    </span>
                    <time dateTime={toIso(instant)} className="font-mono tabular-nums">
                      {formatDateTime(instant)}
                    </time>
                  </>
                ) : null}
              </p>
            ) : null}
          </div>

          {actions || settingsItems.length > 0 ? (
            <div className="flex shrink-0 items-center gap-1">
              {actions}

              {/*
                Drawn only when a page supplied something. A cog opening an empty menu says the
                record has actions and then shows none — the reason `<ProfileCard>` renders its
                own only when non-empty.
              */}
              {settingsItems.length > 0 ? (
                <Menu
                  id={`${id ?? 'profile'}-settings-menu`}
                  items={settingsItems}
                  align="right"
                  trigger={({ open, toggle }) => (
                    <Tooltip label="Settings">
                      <button
                        type="button"
                        id={`${id ?? 'profile'}-settings`}
                        onClick={toggle}
                        aria-haspopup="menu"
                        aria-expanded={open}
                        aria-label="Settings"
                        className="rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                      >
                        <Settings className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </Tooltip>
                  )}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </header>
  );
}

/** One middot-joined line of unlabelled facts. Renders nothing when it has none. */
function FactLine({ facts }: { facts: ReactNode[] }) {
  if (facts.length === 0) return null;

  return (
    <p className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1.5 font-mono text-xs text-slate-500">
      {facts.map((fact, index) => (
        <span key={index} className="flex min-w-0 items-center gap-1.5">
          {index > 0 ? (
            <span className="text-slate-300" aria-hidden="true">
              ·
            </span>
          ) : null}
          <span className="min-w-0 break-words">{fact}</span>
        </span>
      ))}
    </p>
  );
}

/** Case- and whitespace-insensitive, matching `<ProfileIdentity>`'s own guard. */
function sameValue(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}
